/**
 * Edit Class Dialog
 * Form for editing an existing class with conditional field restrictions based on state
 *
 * Field Edit Rules:
 * - classTitle: Always editable
 * - className: Never editable (primary key)
 * - shortClassId: Never editable (used for tagging/reporting)
 * - instructorEmail: Admin-only editable
 * - startTime: Only before any enrollments
 * - endTime: Always editable (can extend)
 * - maxStudents: Admins can change anytime (backend gates decrease by active instance count);
 *                non-admins locked after provisioning (can only increase before provisioning)
 * - templateId/labTypes: Never editable (set at creation, immutable)
 * - classCode: Never editable after creation
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, Lock, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import type { Class, UpdateClassRequest } from '@cloak-next/shared';

// Convert a UTC ISO string to a value suitable for a datetime-local input (local time)
function toLocalInputValue(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface EditClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classData: Class;
  onUpdateClass: (className: string, updates: UpdateClassRequest) => Promise<void>;
  userRole: 'admin' | 'instructor' | 'student';
}

export function EditClassDialog({
  open,
  onOpenChange,
  classData,
  onUpdateClass,
  userRole,
}: EditClassDialogProps) {
  const [formData, setFormData] = useState<{
    classTitle: string;
    endTime: string;
    maxStudents: number;
    instructorEmail: string;
  }>({
    classTitle: classData.classTitle || '',
    endTime: toLocalInputValue(classData.endTime),
    maxStudents: classData.maxStudents,
    instructorEmail: classData.instructorEmail,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine edit restrictions based on class state and role
  const isAdmin = userRole === 'admin';
  const hasEnrollments = classData.currentStudents > 0;
  const hasProvisionedLabs = classData.labsProvisioned;
  const isEnded = classData.status === 'ended';
  // Admins can always edit maxStudents; non-admins are locked after provisioning
  const maxStudentsLocked = hasProvisionedLabs && !isAdmin;

  // Reset form when dialog opens or classData changes
  useEffect(() => {
    if (open) {
      setFormData({
        classTitle: classData.classTitle || '',
        endTime: toLocalInputValue(classData.endTime),
        maxStudents: classData.maxStudents,
        instructorEmail: classData.instructorEmail,
      });
      setErrors({});
    }
  }, [open, classData]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    // Date validation
    const start = new Date(classData.startTime);
    const end = new Date(formData.endTime);

    if (end <= start) {
      newErrors.endTime = 'End time must be after start time';
    }

    // maxStudents validation (only when the field is not locked)
    if (!maxStudentsLocked) {
      if (formData.maxStudents < 1 || formData.maxStudents > 100) {
        newErrors.maxStudents = 'Maximum students must be between 1 and 100';
      }

      // Cannot reduce below current enrollment
      if (hasEnrollments && formData.maxStudents < classData.currentStudents) {
        newErrors.maxStudents = `Cannot reduce below current enrollment (${classData.currentStudents})`;
      }
    }

    // instructorEmail validation (admin only)
    if (isAdmin) {
      if (!formData.instructorEmail) {
        newErrors.instructorEmail = 'Instructor email is required';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.instructorEmail)) {
          newErrors.instructorEmail = 'Please enter a valid email address';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updates: UpdateClassRequest = {};

      // Always include classTitle if changed
      if (formData.classTitle !== (classData.classTitle || '')) {
        updates.classTitle = formData.classTitle || undefined;
      }

      // Always include endTime if changed (convert local input value to UTC ISO for API)
      const endTimeIso = new Date(formData.endTime).toISOString();
      if (endTimeIso !== classData.endTime) {
        updates.endTime = endTimeIso;
      }

      // Include maxStudents if not locked and changed
      if (!maxStudentsLocked && formData.maxStudents !== classData.maxStudents) {
        updates.maxStudents = formData.maxStudents;
      }

      // Include instructorEmail if admin and changed (normalize to lowercase)
      if (isAdmin && formData.instructorEmail.toLowerCase() !== classData.instructorEmail.toLowerCase()) {
        updates.instructorEmail = formData.instructorEmail.toLowerCase();
      }

      // Note: labTypes (templateParameters.selectedLabs) cannot be edited after creation

      // Only submit if there are changes
      if (Object.keys(updates).length === 0) {
        onOpenChange(false);
        return;
      }

      await onUpdateClass(classData.className, updates);
      onOpenChange(false);
    } catch (error: any) {
      setErrors({
        submit: error.message || 'Failed to update class',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper component for locked field indicator
  const LockedFieldTooltip = ({ reason }: { reason: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Lock className="h-4 w-4 text-muted-foreground inline ml-2" />
        </TooltipTrigger>
        <TooltipContent>
          <p>{reason}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Class</DialogTitle>
          <DialogDescription>
            Update class settings. Some fields may be locked based on class state.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}

          {/* State Info Alert */}
          {(hasEnrollments || hasProvisionedLabs || isEnded) && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {isEnded && 'Class has ended. Limited edits available.'}
                {!isEnded && hasProvisionedLabs && !isAdmin && 'Labs are provisioned. Only end time can be changed.'}
                {!isEnded && hasProvisionedLabs && isAdmin && 'Labs are provisioned. As admin, you may still adjust instructor email and maximum students.'}
                {!isEnded && !hasProvisionedLabs && hasEnrollments && 'Students are enrolled. Some fields are restricted.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            {/* Class Name (always locked) */}
            <div className="space-y-2">
              <Label htmlFor="className">
                Class Name (ID)
                <LockedFieldTooltip reason="Class name cannot be changed (used as database key)" />
              </Label>
              <Input
                id="className"
                value={classData.className}
                disabled
                className="bg-muted"
              />
            </div>

            {/* Class Title (always editable) */}
            <div className="space-y-2">
              <Label htmlFor="classTitle">
                Display Title
                <span className="text-muted-foreground text-sm ml-2">
                  (Optional - shown instead of class name)
                </span>
              </Label>
              <Input
                id="classTitle"
                value={formData.classTitle}
                onChange={(e) =>
                  setFormData({ ...formData, classTitle: e.target.value })
                }
                placeholder="e.g., Web Security Workshop - Day 1"
                disabled={isEnded}
              />
            </div>

            {/* Short Class ID (always locked) */}
            <div className="space-y-2">
              <Label htmlFor="shortClassId">
                Short Class ID
                <LockedFieldTooltip reason="Short class ID cannot be changed (used for resource tagging)" />
              </Label>
              <Input
                id="shortClassId"
                value={classData.shortClassId}
                disabled
                className="bg-muted font-mono"
              />
            </div>

            {/* Instructor Email — editable by admins only */}
            <div className="space-y-2">
              <Label htmlFor="instructorEmail">
                Instructor Email
                {!isAdmin && (
                  <LockedFieldTooltip reason="Only admins can reassign class instructor" />
                )}
              </Label>
              <Input
                id="instructorEmail"
                type="email"
                value={isAdmin ? formData.instructorEmail : classData.instructorEmail}
                onChange={(e) =>
                  setFormData({ ...formData, instructorEmail: e.target.value })
                }
                disabled={!isAdmin || isEnded}
                className={!isAdmin ? 'bg-muted' : ''}
                placeholder="instructor@example.com"
              />
              {errors.instructorEmail && (
                <p className="text-sm text-destructive">{errors.instructorEmail}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Start Time (locked after enrollments) */}
              <div className="space-y-2">
                <Label htmlFor="startTime">
                  Start Time
                  {hasEnrollments && <LockedFieldTooltip reason="Cannot change after students have enrolled" />}
                </Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={toLocalInputValue(classData.startTime)}
                  disabled
                  className="bg-muted"
                />
              </div>

              {/* End Time (always editable) */}
              <div className="space-y-2">
                <Label htmlFor="endTime">
                  End Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  disabled={isEnded}
                />
                {errors.endTime && (
                  <p className="text-sm text-destructive">{errors.endTime}</p>
                )}
              </div>
            </div>

            {/* Maximum Students */}
            <div className="space-y-2">
              <Label htmlFor="maxStudents">
                Maximum Students
                {maxStudentsLocked && (
                  <LockedFieldTooltip reason="Cannot change after labs are provisioned" />
                )}
                {!maxStudentsLocked && hasProvisionedLabs && isAdmin && (
                  <span className="text-muted-foreground text-sm ml-2">
                    (Admin override — active provisioned instances set the floor)
                  </span>
                )}
                {!maxStudentsLocked && !hasProvisionedLabs && hasEnrollments && (
                  <span className="text-muted-foreground text-sm ml-2">
                    (Can only increase - current: {classData.currentStudents})
                  </span>
                )}
              </Label>
              <Input
                id="maxStudents"
                type="number"
                min={hasEnrollments ? classData.currentStudents : 1}
                max={100}
                value={formData.maxStudents}
                onChange={(e) =>
                  setFormData({ ...formData, maxStudents: parseInt(e.target.value) || classData.maxStudents })
                }
                disabled={maxStudentsLocked || isEnded}
                className={maxStudentsLocked ? 'bg-muted' : ''}
              />
              {errors.maxStudents && (
                <p className="text-sm text-destructive">{errors.maxStudents}</p>
              )}
            </div>
          </div>

          {/* Environment (read-only - set at creation) */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">Environment</h3>
              <LockedFieldTooltip reason="Lab environment is set at class creation and cannot be changed" />
            </div>
            <div className="border rounded p-3 bg-muted/30">
              <span className="text-sm font-mono">{classData.environmentId || 'N/A'}</span>
            </div>
          </div>

          {/* Enrollment Code (read-only) */}
          <div className="space-y-4">
            <h3 className="font-semibold">Enrollment Settings</h3>
            <div className="space-y-2">
              <Label htmlFor="classCode">
                Enrollment Code
                <LockedFieldTooltip reason="Class code cannot be changed after creation" />
              </Label>
              <Input
                id="classCode"
                value={classData.classCode}
                disabled
                className="bg-muted font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isEnded}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
